import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, CheckCircle2, FileCheck2, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSignupUrl } from '@/lib/appLinks';
import { trackSignupIntent } from '@/lib/tracking';

export const HeroSection: React.FC = () => (
  <section className="field-grid relative overflow-hidden border-b border-border/70 bg-background py-16 sm:py-20 md:py-28">
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-background via-background/95 to-secondary/65" />
    <div className="container relative z-10 grid items-center gap-12 px-4 lg:grid-cols-[1.15fr_.85fr] lg:gap-16">
      <div>
      <div className="eyebrow mb-6"><CheckCircle2 className="h-3.5 w-3.5" /> Built for the field</div>
      <h1 className="mb-7 max-w-4xl text-5xl font-black leading-[0.94] tracking-[-0.055em] sm:text-6xl md:text-7xl">
        SWPPP inspections.<br />
        <span className="text-primary">From site to report.</span>
      </h1>
      <p className="mb-9 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
        Manage your construction sites, record field findings, and generate signed PDF inspection reports in one workspace. Built for consultants handling recurring inspections across multiple projects.
      </p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button size="lg" className="h-12 px-6" asChild>
          <a href={getSignupUrl()} onClick={() => trackSignupIntent('home_hero')}><ArrowRight className="mr-2 h-5 w-5" /> Start your first site free</a>
        </Button>
        <Button size="lg" variant="outline" className="h-12 px-6" asChild>
          <Link to="/demo/sample-inspection"><FileCheck2 className="mr-2 h-5 w-5" /> Try a sample inspection</Link>
        </Button>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">One active site free. Pro: $49/month for up to 5 active sites. <Link to="/pricing" className="text-primary underline">Compare plans</Link></p>
      <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm font-medium text-muted-foreground">
        <Link to="/resources/bmp-field-guide" className="inline-flex items-center gap-2 hover:text-primary"><BookOpen size={16} /> BMP field guide</Link>
        <Link to="/resources/toolbox-talks" className="inline-flex items-center gap-2 hover:text-primary"><Megaphone size={16} /> Toolbox talks</Link>
        <Link to="/resources/templates" className="inline-flex items-center gap-2 hover:text-primary">Free templates <ArrowRight size={16} /></Link>
      </div>
      </div>

      <div className="relative mx-auto w-full max-w-lg lg:mx-0">
        <div className="absolute -inset-4 -rotate-2 rounded-[2rem] border border-primary/10 bg-primary/5" />
        <div className="relative overflow-hidden rounded-[1.5rem] border border-border bg-card shadow-2xl shadow-foreground/10">
          <div className="flex items-center justify-between border-b border-border bg-secondary/45 px-5 py-4">
            <div>
              <p className="section-label">Inspection workflow</p>
              <p className="mt-1 font-bold">Keep the field and office connected</p>
            </div>
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">READY</span>
          </div>
          <div className="space-y-3 p-5 sm:p-6">
            {[
              ['Set up your site', 'Keep active projects in one workspace'],
              ['Record your findings', 'Document controls and inspection notes'],
              ['Finish your report', 'Sign and download a PDF inspection report'],
            ].map(([title, detail], index) => (
              <div key={title} className="flex gap-4 rounded-xl border border-border/70 bg-background/70 p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-black text-primary-foreground">{index + 1}</div>
                <div><p className="font-bold">{title}</p><p className="mt-0.5 text-sm text-muted-foreground">{detail}</p></div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 border-t border-border bg-primary/[.045] px-6 py-4 text-sm font-medium text-primary">
            <FileCheck2 className="h-4 w-4" /> Try the sample inspection without an account
          </div>
        </div>
      </div>
    </div>
  </section>
);
